import React, { useState } from "react";

import { faEdit, faTable } from "@fortawesome/pro-regular-svg-icons";

import { EditableTableBlock } from "src/cases/caseView/blocks/EditableTableBlock";
import { MultiRecordDisplay } from "src/cases/caseView/blocks/MultiRecordDisplay";
import { SingleRecordDisplay } from "src/cases/caseView/blocks/SingleRecordDisplay";
import {
  UseTableBlockEditsReturn,
  useTableBlockEdits,
} from "src/cases/caseView/blocks/useTableBlockEdits";
import { CaseBlock, CaseBlockProps } from "src/cases/caseView/CaseBlock";
import { useSaveBlockData } from "src/cases/queries/cases";
import {
  CaseDeserialized,
  TableBlockMultiColumnRecord,
  TableBlockRecord,
} from "src/cases/queries/casesTypes";
import {
  Config1,
  ModelsCaseDataApiModelsTableBlockDataPatchDataTypeEnum,
  Payload,
  TableEditMode,
} from "src/clients/case-management";
import { Button } from "src/design-system/Button";
import { EmptyState } from "src/design-system/EmptyState";
import { useCapabilities } from "src/hooks/useCapabilities";
import { CasePageParams } from "src/router/urls";
import { useCurrentUserId } from "src/store/AuthStore";
import { useParamsDecode } from "src/utils/useParamsDecode";

export type TableBlockData = {
  record: TableBlockRecord;
  etag?: string;
};

type TableConfig = Extract<Config1, { kind: "table" }>;

type TableBlockProps = Omit<CaseBlockProps, "children"> & {
  data: TableBlockData;
  config?: TableConfig;
  caseData?: CaseDeserialized;
};

const isMultiColumnRecord = (
  record: TableBlockRecord,
): record is TableBlockMultiColumnRecord => Array.isArray(record);

// Rows and etag captured together when editing starts — polling keeps
// advancing live props, and a fresh etag paired with stale rows would
// silently overwrite another user's changes. Cell heights are captured so
// Cancel reverts them along with the data.
type EditSession = {
  record: TableBlockMultiColumnRecord;
  etag?: string;
  cellHeights: UseTableBlockEditsReturn["cellHeights"];
};

export const TableBlock: React.FC<TableBlockProps> = ({
  data,
  config,
  caseData,
  ...props
}) => {
  const [editSession, setEditSession] = useState<EditSession | null>(null);
  const { case_id: caseId } = useParamsDecode<CasePageParams>();
  const { cases } = useCapabilities();
  const currentUserId = useCurrentUserId();
  const saveBlockDataMutation = useSaveBlockData();

  const isMultiRecord = isMultiColumnRecord(data.record);

  // Admins can always edit blocks; others only when assigned to the case.
  const canEditBlock =
    cases.canManage ||
    Boolean(
      currentUserId &&
        cases.canEditBlocks &&
        caseData?.data.assignee?.id === currentUserId,
    );

  const isEditable =
    isMultiRecord &&
    config?.edit_mode === TableEditMode.EDITABLE &&
    canEditBlock;

  const multiRecords = isMultiRecord
    ? (data.record as TableBlockMultiColumnRecord)
    : [];
  const edits = useTableBlockEdits(editSession?.record ?? multiRecords);

  const isEmpty = isMultiRecord
    ? data.record.length === 0
    : Object.keys(data.record).length === 0;

  if (isEmpty) {
    return (
      <CaseBlock {...props}>
        <EmptyState
          description="This table has no data to display"
          headline="No data available"
          icon={faTable}
          size="compact"
        />
      </CaseBlock>
    );
  }

  if (!isMultiColumnRecord(data.record)) {
    return (
      <CaseBlock {...props}>
        <SingleRecordDisplay record={data.record} />
      </CaseBlock>
    );
  }

  const handleCancel = () => {
    if (editSession) {
      edits.restoreCellHeights(editSession.cellHeights);
    }
    edits.resetEdits();
    setEditSession(null);
  };

  const handleSave = () => {
    if (!caseId || !editSession) return;

    const payload: Payload = {
      data: {
        type: ModelsCaseDataApiModelsTableBlockDataPatchDataTypeEnum.CASE_BLOCK_DATA,
        id: props.blockKey,
        attributes: {
          record: edits.editedData,
        },
      },
    };

    saveBlockDataMutation.mutate(
      {
        caseId,
        blockKey: props.blockKey,
        blockTitle: props.title,
        payload,
        blockEtag: editSession.etag ?? "0",
      },
      {
        onSuccess: () => {
          edits.commitEdits();
          setEditSession(null);
        },
      },
    );
  };

  const editingActions = (
    <div className="flex items-center gap-3">
      <Button
        dataLoc="table-block-cancel-button"
        isDisabled={saveBlockDataMutation.isPending}
        size="sm"
        variant="secondary"
        onClick={handleCancel}
      >
        Cancel
      </Button>
      <Button
        dataLoc="table-block-save-button"
        isDisabled={!edits.hasChanges || saveBlockDataMutation.isPending}
        isLoading={saveBlockDataMutation.isPending}
        size="sm"
        variant="primary"
        onClick={handleSave}
      >
        Save
      </Button>
    </div>
  );

  const editButton = (
    <Button
      dataLoc="table-block-edit-button"
      iconLeft={faEdit}
      size="sm"
      variant="secondary"
      onClick={() =>
        setEditSession({
          record: multiRecords,
          etag: data.etag,
          cellHeights: edits.cellHeights,
        })
      }
    >
      Edit
    </Button>
  );

  const rightSlot = editSession
    ? editingActions
    : isEditable
      ? editButton
      : undefined;

  return (
    <CaseBlock
      {...props}
      expandedRightSlot={rightSlot}
      isExpandable={true}
      noPaddings={true}
      noScroll={true}
      rightSlot={rightSlot}
    >
      {editSession ? (
        <EditableTableBlock
          columnsConfig={config?.columns}
          defaultColumnsEditMode={config?.default_columns_edit_mode}
          edits={edits}
          records={editSession.record}
        />
      ) : (
        <MultiRecordDisplay
          columnsConfig={config?.columns}
          getCellHeight={edits.getCellHeight}
          records={data.record}
          onCellResize={edits.setCellHeight}
        />
      )}
    </CaseBlock>
  );
};
